<div class="dashboard-header">
  <div>
    <h2>{{ 'dashboard.title' | translate }}</h2>
    <p>{{ 'dashboard.description' | translate }}</p>
  </div>
  <div class="dashboard-controls">
    <span
      class="date-reset-tooltip-trigger"
      [matTooltip]="'dashboard.actions.reset_dates' | translate"
      matTooltipPosition="below"
    >
      <button
        type="button"
        class="date-reset-btn"
        [attr.aria-label]="'dashboard.actions.reset_dates' | translate"
        [disabled]="!canResetDateFilters()"
        (click)="resetDateFilters()"
      >
        <mat-icon>restart_alt</mat-icon>
      </button>
    </span>
    <div class="dashboard-date-filter">
      <mat-form-field class="dashboard-date-field" appearance="outline">
        <mat-label>{{ 'dashboard.filters.date_from' | translate }}</mat-label>
        <input
          matInput
          [matDatepicker]="dashboardDateFromPicker"
          [value]="selectedDashboardDateFromValue()"
          (dateChange)="onDashboardDateFromChange($event.value)"
        />
        <mat-datepicker-toggle
          matSuffix
          [for]="dashboardDateFromPicker"
        ></mat-datepicker-toggle>
        <mat-datepicker #dashboardDateFromPicker></mat-datepicker>
      </mat-form-field>
    </div>
    <div class="dashboard-date-filter">
      <mat-form-field class="dashboard-date-field" appearance="outline">
        <mat-label>{{ 'dashboard.filters.date_to' | translate }}</mat-label>
        <input
          matInput
          [matDatepicker]="dashboardDateToPicker"
          [value]="selectedDashboardDateToValue()"
          (dateChange)="onDashboardDateToChange($event.value)"
        />
        <mat-datepicker-toggle
          matSuffix
          [for]="dashboardDateToPicker"
        ></mat-datepicker-toggle>
        <mat-datepicker #dashboardDateToPicker></mat-datepicker>
      </mat-form-field>
    </div>
    <app-button
      (click)="loadDashboardData()"
      [title]="'dashboard.actions.reload' | translate"
      [small]="true"
    ></app-button>
  </div>
</div>
<hr />
<div class="-box">
  <p *ngIf="dashboardDataStore.loading()" class="status">
    {{ 'dashboard.loading' | translate }}
  </p>
  <div *ngIf="dashboardDataStore.loadError()" class="error">
    <p>{{ dashboardDataStore.errorMessage() ?? '' | translate }}</p>
    <small *ngIf="dashboardDataStore.errorStatusLabel()" class="error-status">
      {{ dashboardDataStore.errorStatusLabel() }}
    </small>
    <small *ngIf="dashboardDataStore.errorDetail()" class="error-detail">
      {{ dashboardDataStore.errorDetail() }}
    </small>
    <app-button
      *ngIf="dashboardDataStore.isUnauthorized()"
      (click)="goToLogin()"
      [title]="'dashboard.actions.go_to_login' | translate"
      [small]="true"
    ></app-button>
  </div>
  <div
    *ngIf="
      !dashboardDataStore.loading() &&
      !dashboardDataStore.loadError() &&
      dashboardDataStore.hasPartialFailures()
    "
    class="warning"
  >
    {{ 'dashboard.messages.partial_sections_failed' | translate }}
  </div>

  <section
    *ngIf="
      !dashboardDataStore.loading() &&
      !dashboardDataStore.loadError() &&
      !dashboardDataStore.briefLoadError()
    "
    class="summary-grid"
  >
    <article
      class="summary-segmentation-card"
      data-testid="dashboard-total-flight-time"
      [style.--card-accent]="'#0d9488'"
    >
      <div class="header">
        <h3>{{ 'dashboard.cards.total_flight_time.title' | translate }}</h3>
        <small
          >({{ 'dashboard.labels.hours' | translate }}){{
            selectedFlightDateRangeSuffix()
          }}</small
        >
      </div>
      <div class="divider"></div>
      <div class="value-wrap" data-testid="dashboard-total-flight-time-value">
        {{ dashboardDataStore.totalFlightHoursValue() | number: '1.2-2' }}
        {{ 'dashboard.labels.hours' | translate }}
      </div>

      <div
        *ngIf="
          canShowFlightTimeBreakdownChart();
          else timeSegmentationUnavailable
        "
        class="flight-breakdown-chart-wrap"
      >
        <div
          #flightTimeBreakdownChartHost
          class="flight-time-breakdown-pie"
        ></div>
      </div>
      <ng-template #timeSegmentationUnavailable>
        <small class="segmentation-unavailable">
          {{ flightTimeBreakdownUnavailableReason() ?? '' | translate }}
        </small>
      </ng-template>
    </article>

    <article
      class="summary-segmentation-card"
      data-testid="dashboard-total-flights"
      [style.--card-accent]="'#0f766e'"
    >
      <div class="header">
        <h3>{{ 'dashboard.cards.total_flights.title' | translate }}</h3>
        <small
          >({{ 'dashboard.labels.count' | translate }}){{
            selectedFlightDateRangeSuffix()
          }}</small
        >
      </div>
      <div class="divider"></div>
      <div class="value-wrap" data-testid="dashboard-total-flights-value">
        {{ dashboardDataStore.brief().totalFlightsCount }}
      </div>

      <div
        *ngIf="canShowFlightCountBreakdownChart(); else segmentationUnavailable"
        class="flight-breakdown-chart-wrap"
      >
        <div
          #flightCountBreakdownChartHost
          class="flight-count-breakdown-radial"
        ></div>
      </div>
      <ng-template #segmentationUnavailable>
        <small class="segmentation-unavailable">
          {{ flightCountBreakdownUnavailableReason() ?? '' | translate }}
        </small>
      </ng-template>
    </article>

    <div class="summary-side-stack">
      <article
        class="summary-segmentation-card summary-metric-card"
        [style.--card-accent]="'#0284c7'"
      >
        <div class="header">
          <h3>
            {{ 'dashboard.cards.avg_fleet_flight_hours.title' | translate }}
          </h3>
          <small
            >({{ 'dashboard.labels.hours' | translate }}){{
              selectedFlightDateRangeSuffix()
            }}</small
          >
        </div>
        <div class="divider"></div>
        <div class="value-wrap">
          {{ dashboardDataStore.avgFleetFlightHoursValue() | number: '1.2-2' }}
          {{ 'dashboard.labels.hours' | translate }}
        </div>
      </article>

      <article
        class="summary-segmentation-card summary-drone-card"
        data-testid="dashboard-most-used-drone"
        [style.--card-accent]="'#0f766e'"
      >
        <div class="header">
          <h3>{{ 'dashboard.cards.most_used_drone.title' | translate }}</h3>
          <small
            >({{ 'dashboard.labels.flight_hours' | translate }}){{
              selectedFlightDateRangeSuffix()
            }}</small
          >
        </div>
        <div class="divider"></div>
        <ng-container *ngIf="mostUsedDrone() as mostUsed; else noMostUsedCard">
          <div class="value-wrap">
            {{ mostUsed.flightHours | number: '1.2-2' }}
            {{ 'dashboard.labels.hours' | translate }}
          </div>
          <div class="drone-meta">{{ mostUsed.uavLabel }}</div>
          <div class="drone-meta">
            {{ mostUsed.flightsCount }}
            {{ 'dashboard.labels.flights' | translate }}
          </div>
        </ng-container>
        <ng-template #noMostUsedCard>
          <div class="value-wrap">-</div>
          <div class="drone-meta">
            {{ 'dashboard.labels.no_data' | translate }}
          </div>
        </ng-template>
      </article>

      <article
        class="summary-segmentation-card summary-drone-card"
        data-testid="dashboard-least-used-drone"
        [style.--card-accent]="'#ea580c'"
      >
        <div class="header">
          <h3>{{ 'dashboard.cards.least_used_drone.title' | translate }}</h3>
          <small
            >({{ 'dashboard.labels.flight_hours' | translate }}){{
              selectedFlightDateRangeSuffix()
            }}</small
          >
        </div>
        <div class="divider"></div>
        <ng-container
          *ngIf="leastUsedDrone() as leastUsed; else noLeastUsedCard"
        >
          <div class="value-wrap">
            {{ leastUsed.flightHours | number: '1.2-2' }}
            {{ 'dashboard.labels.hours' | translate }}
          </div>
          <div class="drone-meta">{{ leastUsed.uavLabel }}</div>
          <div class="drone-meta">
            {{ leastUsed.flightsCount }}
            {{ 'dashboard.labels.flights' | translate }}
          </div>
        </ng-container>
        <ng-template #noLeastUsedCard>
          <div class="value-wrap">-</div>
          <div class="drone-meta">
            {{ 'dashboard.labels.no_data' | translate }}
          </div>
        </ng-template>
      </article>
    </div>
  </section>
  <div
    *ngIf="
      !dashboardDataStore.loading() &&
      !dashboardDataStore.loadError() &&
      dashboardDataStore.briefLoadError()
    "
    class="section-warning"
  >
    {{ 'dashboard.messages.summary_metrics_unavailable' | translate }}
  </div>

  <section
    *ngIf="!dashboardDataStore.loading() && !dashboardDataStore.loadError()"
    class="lists-grid"
  >
    <app-dashboard-flight-list
      *ngIf="
        !dashboardDataStore.highestFlightsLoadError();
        else highestFlightsError
      "
      data-testid="dashboard-highest-flight-time"
      [title]="'dashboard.lists.highest_flight_time.title' | translate"
      [subtitle]="
        ('dashboard.labels.top_10' | translate) +
        selectedFlightDateRangeSuffix()
      "
      [items]="dashboardDataStore.highestFlightItems()"
      [totalFlightSeconds]="dashboardDataStore.brief().totalFlightSeconds"
      [accent]="'#0f766e'"
    ></app-dashboard-flight-list>
    <ng-template #highestFlightsError>
      <article class="section-warning-card">
        {{ 'dashboard.messages.highest_list_unavailable' | translate }}
      </article>
    </ng-template>

    <app-dashboard-flight-list
      *ngIf="
        !dashboardDataStore.lowestFlightsLoadError();
        else lowestFlightsError
      "
      data-testid="dashboard-lowest-flight-time"
      [title]="'dashboard.lists.lowest_flight_time.title' | translate"
      [subtitle]="
        ('dashboard.labels.bottom_10' | translate) +
        selectedFlightDateRangeSuffix()
      "
      [items]="dashboardDataStore.lowestFlightItems()"
      [totalFlightSeconds]="dashboardDataStore.brief().totalFlightSeconds"
      [accent]="'#ea580c'"
    ></app-dashboard-flight-list>
    <ng-template #lowestFlightsError>
      <article class="section-warning-card">
        {{ 'dashboard.messages.lowest_list_unavailable' | translate }}
      </article>
    </ng-template>

    <app-dashboard-service-due-list
      *ngIf="
        !dashboardDataStore.serviceDuePerDroneLoadError();
        else serviceDuePerDroneError
      "
      [title]="'dashboard.lists.service_due_per_drone.title' | translate"
      [subtitle]="'dashboard.labels.top_10' | translate"
      [items]="dashboardDataStore.serviceDuePerDroneItems()"
      [leadingColumn]="'uav'"
      [accent]="'var(--quantum-black)'"
    ></app-dashboard-service-due-list>
    <ng-template #serviceDuePerDroneError>
      <article class="section-warning-card service-due-per-drone-error">
        {{ 'dashboard.messages.service_due_per_drone_unavailable' | translate }}
      </article>
    </ng-template>

    <app-dashboard-service-due-list
      *ngIf="
        !dashboardDataStore.serviceDuePerPartLoadError();
        else serviceDuePerPartError
      "
      [title]="'dashboard.lists.service_due_per_part.title' | translate"
      [subtitle]="'dashboard.labels.top_10' | translate"
      [items]="dashboardDataStore.serviceDuePerPartItems()"
      [leadingColumn]="'partId'"
      [accent]="'var(--quantum-black)'"
    ></app-dashboard-service-due-list>
    <ng-template #serviceDuePerPartError>
      <article class="section-warning-card service-due-per-part-error">
        {{ 'dashboard.messages.service_due_per_part_unavailable' | translate }}
      </article>
    </ng-template>

    <app-dashboard-service-done-list
      *ngIf="!dashboardDataStore.serviceDoneLoadError(); else serviceDoneError"
      [title]="'dashboard.lists.service_done.title' | translate"
      [subtitle]="'dashboard.labels.top_10' | translate"
      [items]="dashboardDataStore.serviceDoneItems()"
      [accent]="'var(--quantum-black)'"
    ></app-dashboard-service-done-list>
    <ng-template #serviceDoneError>
      <article class="section-warning-card service-done-error">
        {{ 'dashboard.messages.service_done_unavailable' | translate }}
      </article>
    </ng-template>
  </section>
</div>
